// Generic tooltip engine: one step list drives both the guided walkthrough
// (driver.js) and the hover/focus tips, so copy can never drift between them.
// Steps come from the DB (admin-authored) instead of being hardcoded per page.
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { tooltipSelector, type TooltipRow } from "./types";

const isPresent = (sel: string) => Boolean(document.querySelector(sel));

const clickAnchor = (key: string) =>
  (document.querySelector(`[data-tour-open="${key}"]`) as HTMLElement | null)?.click();

/** Drive an aria-expanded toggle (e.g. the Clip Studio settings panel). */
function setExpanded(key: string, open: boolean) {
  const btn = document.querySelector(`[data-tour="${key}"]`) as HTMLElement | null;
  if (!btn) return;
  if ((btn.getAttribute("aria-expanded") === "true") !== open) btn.click();
}

export function runTour(steps: TooltipRow[], hooks: { onFinish: () => void }) {
  const usable = steps.filter((s) => isPresent(tooltipSelector(s)) || Boolean(s.next_action));
  if (!usable.length) {
    hooks.onFinish();
    return;
  }

  let d: ReturnType<typeof driver>;

  // Run the scripted action, let React paint, then advance.
  const advance = (s: TooltipRow) => {
    const a = s.next_action!;
    if (a.click) clickAnchor(a.click);
    if (a.expandKey && a.expand) setExpanded(a.expandKey, a.expand === "open");
    window.setTimeout(() => d.moveNext(), a.delayMs ?? 380);
  };

  d = driver({
    showProgress: true,
    allowClose: true,
    // Backdrop clicks do nothing — the tour closes via × or Escape only.
    overlayClickBehavior: () => undefined,
    popoverClass: "podgo-tour",
    nextBtnText: "Next →",
    prevBtnText: "← Back",
    doneBtnText: "Got it",
    onDestroyed: () => {
      hooks.onFinish();
    },
    steps: usable.map((s) => ({
      element: tooltipSelector(s),
      popover: {
        title: s.title,
        description: s.description,
        side: s.side,
        align: s.align,
        ...(s.next_action ? { onNextClick: () => advance(s) } : {}),
      },
    })),
  });

  d.drive();
}

// --- Hover / focus tips ----------------------------------------------------
// Same steps, shown on hover or keyboard focus for returning users.
let teardown: (() => void) | null = null;

export function initHoverTips(steps: TooltipRow[]) {
  stopHoverTips();
  if (typeof document === "undefined" || !steps.length) return;

  const tip = document.createElement("div");
  tip.className = "hovertip";
  tip.setAttribute("role", "tooltip");
  tip.style.display = "none";
  const titleEl = document.createElement("div");
  titleEl.className = "hovertip-title";
  const descEl = document.createElement("div");
  descEl.className = "hovertip-desc";
  tip.append(titleEl, descEl);
  document.body.appendChild(tip);

  let current: HTMLElement | null = null;

  function place(target: HTMLElement) {
    tip.style.display = "block";
    const r = target.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    let top = r.bottom + 8;
    if (top + th > window.innerHeight - 8) top = r.top - th - 8; // flip above if it would clip
    const left = Math.max(8, Math.min(r.left, window.innerWidth - tw - 8));
    tip.style.top = `${Math.max(8, top)}px`;
    tip.style.left = `${left}px`;
  }

  /** Find the step whose selector matches the closest ancestor of the event target. */
  function match(el: HTMLElement | null): { step: TooltipRow; node: HTMLElement } | null {
    if (!el) return null;
    for (const s of steps) {
      const node = el.closest(tooltipSelector(s)) as HTMLElement | null;
      if (node) return { step: s, node };
    }
    return null;
  }

  const show = (el: HTMLElement | null) => {
    const hit = match(el);
    if (!hit || hit.node === current) return;
    current = hit.node;
    titleEl.textContent = hit.step.title;
    descEl.textContent = hit.step.description;
    place(hit.node);
  };

  const hide = () => {
    tip.style.display = "none";
    current = null;
  };

  const onOver = (e: Event) => show(e.target as HTMLElement);
  const onOut = (e: Event) => {
    const hit = match(e.target as HTMLElement);
    if (hit && hit.node === current) hide();
  };

  document.addEventListener("mouseover", onOver);
  document.addEventListener("mouseout", onOut);
  document.addEventListener("focusin", onOver);
  document.addEventListener("focusout", hide);

  teardown = () => {
    document.removeEventListener("mouseover", onOver);
    document.removeEventListener("mouseout", onOut);
    document.removeEventListener("focusin", onOver);
    document.removeEventListener("focusout", hide);
    tip.remove();
  };
}

export function stopHoverTips() {
  teardown?.();
  teardown = null;
}
